import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components/ensigns';
import { SpecraInlineBanner, SpecraSectionHeader } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTenantAreas } from '../../../lib/access/useTenantAreas';
import { PricingSheetIngestProgress } from '../../Delivery/Registries/PricingSheetIngestProgress';
import { PricingSheetUploadDialog } from '../../Delivery/Registries/PricingSheetUploadDialog';
import { useActiveIngests } from '../../Delivery/Registries/useActiveIngests';
import { useCanUploadPricingSheet } from '../../Delivery/Registries/usePricingSheetUploadGate';
import { VehicleProfileEditor } from '../../Delivery/Registries/VehicleProfileEditor';
import { useVehicleProfile, useVehicleViewer } from '../../Delivery/Registries/vehicleIdentityHooks';
import { useRecordDocuments } from '../documents/hooks';
import { documentsQueryState } from '../documents/recordDocumentsSection';
import { RegistryRecordFrame } from '../RegistryRecordFrame';
import { HoldersTable } from './vehicle/HoldersTable';
import { HoldingsAndConnectedWork } from './vehicle/HoldingsAndConnectedWork';
import { useVehicleDetails, useVehicleDossier } from './vehicle/hooks';
import { LcatsOnVehicle } from './vehicle/LcatsOnVehicle';
import { LineageEditorDialog } from './vehicle/LineageEditorDialog';
import { PricingSheetsKept } from './vehicle/PricingSheetsKept';
import { TaskOrderRounds } from './vehicle/TaskOrderRounds';
import { VehicleHeader } from './vehicle/VehicleHeader';
import { vehicleRail } from './vehicle/vehicleRail';
function VehicleProfileSection({ id, onSaved }) {
    const profile = useVehicleProfile(id);
    const canWrite = useTenantAreas().registries;
    const [editing, setEditing] = useState(false);
    if (!canWrite) return null;
    if (profile.isPending) return /*#__PURE__*/ _jsx("output", {
        children: "Loading vehicle profile…"
    });
    if (profile.isError) return /*#__PURE__*/ _jsx(SpecraInlineBanner, {
        tone: "danger",
        action: /*#__PURE__*/ _jsx(SpecraButton, {
            onClick: ()=>void profile.refetch(),
            children: "Retry profile"
        }),
        children: "Could not load vehicle profile."
    });
    if (!profile.data?.vehicle || profile.data.vehicle.isSystemSeed) return null;
    return /*#__PURE__*/ _jsxs("section", {
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                variant: "group",
                title: "Vehicle profile"
            }),
            editing ? /*#__PURE__*/ _jsx(VehicleProfileEditor, {
                profile: profile.data,
                onCancel: ()=>setEditing(false),
                onSaved: ()=>{
                    setEditing(false);
                    onSaved();
                },
                onReload: async ()=>{
                    await profile.refetch();
                    setEditing(false);
                }
            }, String(profile.data.vehicle.revision)) : /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "secondary",
                onClick: ()=>setEditing(true),
                children: "Edit profile"
            })
        ]
    });
}
export function VehicleRecordPage() {
    const { id = '' } = useParams();
    return /*#__PURE__*/ _jsx(VehicleRecord, {
        id: id
    }, id);
}
function VehicleRecord({ id }) {
    const query = useVehicleDossier(id);
    const d = query.data;
    const year = new Date().getUTCFullYear();
    const { holders, rounds } = useVehicleDetails(id, !!d, year);
    const documents = useRecordDocuments({
        kind: 'VEHICLE',
        id
    }, !!d);
    const canWrite = useTenantAreas().registries;
    const canUpload = useCanUploadPricingSheet();
    const [lineageOpen, setLineageOpen] = useState(false);
    const [uploadOpen, setUploadOpen] = useState(false);
    const viewer = useVehicleViewer();
    const { runs, track, untrack } = useActiveIngests(viewer.tenant);
    return /*#__PURE__*/ _jsx(RegistryRecordFrame, {
        kindLabel: "Contract Vehicles",
        tab: "vehicles",
        title: d?.name,
        status: query.isPending ? 'loading' : query.isError ? 'error' : d ? 'ready' : 'unavailable',
        error: query.error,
        onRetry: ()=>void query.refetch(),
        header: d && /*#__PURE__*/ _jsx(VehicleHeader, {
            dossier: d,
            onUpload: canUpload ? ()=>setUploadOpen(true) : undefined
        }),
        railSections: d ? vehicleRail(d, holders.data, canWrite ? ()=>setLineageOpen(true) : undefined, documents.data, documentsQueryState(documents)) : undefined,
        children: d && /*#__PURE__*/ _jsxs("div", {
            className: "space-y-8",
            "data-testid": Testids.registryVehicle.page,
            children: [
                rounds.isPending ? /*#__PURE__*/ _jsx("output", {
                    children: "Loading task-order rounds…"
                }) : rounds.isError ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    action: /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>void rounds.refetch(),
                        children: "Retry rounds"
                    }),
                    children: "Could not load task-order rounds."
                }) : /*#__PURE__*/ _jsx(TaskOrderRounds, {
                    data: rounds.data,
                    dossier: d,
                    year: year
                }),
                holders.isPending ? /*#__PURE__*/ _jsx("output", {
                    children: "Loading holders…"
                }) : holders.isError ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    action: /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>void holders.refetch(),
                        children: "Retry holders"
                    }),
                    children: "Could not load holders."
                }) : /*#__PURE__*/ _jsx(HoldersTable, {
                    data: holders.data,
                    name: d.name
                }),
                /*#__PURE__*/ _jsx(HoldingsAndConnectedWork, {
                    vehicleId: id
                }),
                /*#__PURE__*/ _jsx(PricingSheetsKept, {
                    sheets: d.pricingSheets
                }),
                /*#__PURE__*/ _jsx(LcatsOnVehicle, {
                    dossier: d
                }),
                /*#__PURE__*/ _jsx(VehicleProfileSection, {
                    id: id,
                    onSaved: ()=>void query.refetch()
                }),
                lineageOpen && canWrite && /*#__PURE__*/ _jsx(LineageEditorDialog, {
                    vehicleId: id,
                    canWrite: canWrite,
                    onClose: ()=>setLineageOpen(false)
                }),
                canUpload && /*#__PURE__*/ _jsx(PricingSheetUploadDialog, {
                    open: uploadOpen,
                    onOpenChange: setUploadOpen,
                    expectedVehicleId: id,
                    onStarted: (runId, filename)=>{
                        setUploadOpen(false);
                        track(runId, filename);
                    }
                }),
                runs.map((run)=>/*#__PURE__*/ _jsx(PricingSheetIngestProgress, {
                        runId: run.runId,
                        filename: run.filename,
                        onSettled: ()=>{
                            untrack(run.runId);
                            void query.refetch();
                        }
                    }, run.runId))
            ]
        })
    });
}
